// @tier: PREMIUM — Vue admin des incidents chauffeur
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdownMenu';
import { StatusBadge } from '../components/ui/StatusBadge';
import {
  Search,
  Filter,
  RefreshCw,
  Eye,
  CheckCircle2,
  Calendar,
  MoreHorizontal,
  Inbox,
  Loader2,
  User,
  Clock,
  Wrench,
  AlertTriangle,
  Users,
  HeartPulse,
  HelpCircle,
  FileText,
  Image as ImageIcon,
} from 'lucide-react';

import useIncidents from '../hooks/trIncidents';

const TYPE_CONFIG = {
  delay: { label: 'Retard', icon: Clock },
  breakdown: { label: 'Panne', icon: Wrench },
  accident: { label: 'Accident', icon: AlertTriangle },
  behavior: { label: 'Comportement', icon: Users },
  medical: { label: 'Médical', icon: HeartPulse },
  other: { label: 'Autre', icon: HelpCircle },
};

const getTypeConfig = (type) => TYPE_CONFIG[type] ?? TYPE_CONFIG.other;

const getDriverName = (incident) =>
  `${incident.drivers?.first_name || ''} ${incident.drivers?.last_name || ''}`.trim() || '—';

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const IncidentsManagementPremium = () => {
  const {
    filteredIncidents,
    stats,
    loading,
    loadData,
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus,
    selectedIncident,
    showDetailDialog,
    loadingDetail,
    onDetailDialogOpenChange,
    showActionDialog,
    onActionDialogOpenChange,
    actionStatus,
    setActionStatus,
    actionNote,
    setActionNote,
    handleViewDetails,
    handleUpdateStatus,
    confirmUpdateStatus,
  } = useIncidents();

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Incidents Chauffeur</h1>
          <p className="text-gray-600 mt-1">Signalements soumis par les chauffeurs depuis l'application mobile</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Inbox, bg: 'bg-blue-100', text: 'text-blue-600', label: 'Total', value: stats.total },
          { icon: AlertTriangle, bg: 'bg-red-100', text: 'text-red-600', label: 'Ouverts', value: stats.open },
          { icon: Clock, bg: 'bg-blue-100', text: 'text-blue-600', label: 'Pris en compte', value: stats.acknowledged },
          { icon: CheckCircle2, bg: 'bg-green-100', text: 'text-green-600', label: 'Résolus', value: stats.resolved },
        ].map(({ icon: Icon, bg, text, label, value }) => (
          <div
            key={label}
            className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center space-x-4"
          >
            <div className={`p-3 ${bg} rounded-lg ${text}`}>
              <Icon size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">{label}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par chauffeur ou description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { value: 'all', label: 'Tous', active: 'bg-blue-100 text-blue-800' },
                { value: 'open', label: 'Ouverts', active: 'bg-red-100 text-red-800' },
                { value: 'acknowledged', label: 'Pris en compte', active: 'bg-blue-100 text-blue-800' },
                { value: 'resolved', label: 'Résolus', active: 'bg-green-100 text-green-800' },
              ].map(({ value, label, active }) => (
                <button
                  key={value}
                  onClick={() => setFilterStatus(value)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filterStatus === value ? active : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={loadData}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Vue cartes — mobile uniquement */}
        <div className="md:hidden divide-y divide-gray-100">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Chargement...</div>
          ) : filteredIncidents.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Aucun incident trouvé</div>
          ) : (
            filteredIncidents.map((incident) => {
              const TypeIcon = getTypeConfig(incident.type).icon;
              return (
                <div key={incident.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{getDriverName(incident)}</p>
                      <p className="text-xs text-gray-500 truncate max-w-[220px]">{incident.description}</p>
                    </div>
                    <StatusBadge status={incident.status} />
                  </div>
                  <div className="mt-2 ml-0 text-xs text-gray-500 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1">
                      <TypeIcon className="h-3 w-3" /> {getTypeConfig(incident.type).label}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {formatDate(incident.created_at)}
                    </span>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          aria-label="Actions"
                          className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        >
                          <MoreHorizontal size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => handleViewDetails(incident)}>
                          <Eye size={14} className="mr-2" />
                          Voir les détails
                        </DropdownMenuItem>
                        {incident.status !== 'resolved' && (
                          <DropdownMenuItem onClick={() => handleUpdateStatus(incident)}>
                            <CheckCircle2 size={14} className="mr-2" />
                            Mettre à jour le statut
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Tableau — desktop uniquement */}
        <div className="overflow-x-auto hidden md:block">
          <table className="min-w-[780px] w-full table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Chauffeur
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-4 py-3 w-28 whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Chargement...
                  </td>
                </tr>
              ) : filteredIncidents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun incident trouvé</h3>
                    <p className="text-sm text-gray-500">
                      {searchTerm || filterStatus !== 'all'
                        ? 'Aucun incident ne correspond aux critères de recherche.'
                        : "Aucun incident n'a encore été signalé par un chauffeur."}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredIncidents.map((incident) => {
                  const TypeIcon = getTypeConfig(incident.type).icon;
                  return (
                    <tr key={incident.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                            <User className="text-blue-600" size={20} />
                          </div>
                          <div className="ml-4 min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">{getDriverName(incident)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <TypeIcon className="h-3 w-3" /> {getTypeConfig(incident.type).label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 truncate max-w-[240px]">{incident.description}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(incident.created_at)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <StatusBadge status={incident.status} />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                aria-label="Actions"
                                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                              >
                                <MoreHorizontal size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem onClick={() => handleViewDetails(incident)}>
                                <Eye size={14} className="mr-2" />
                                Voir les détails
                              </DropdownMenuItem>
                              {incident.status !== 'resolved' && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(incident)}>
                                  <CheckCircle2 size={14} className="mr-2" />
                                  Mettre à jour le statut
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Détails */}
      <Dialog open={showDetailDialog} onOpenChange={onDetailDialogOpenChange}>
        <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Détails de l'Incident</DialogTitle>
            <DialogDescription>{selectedIncident && getDriverName(selectedIncident)}</DialogDescription>
          </DialogHeader>
          {selectedIncident && (
            <div className="space-y-6 overflow-y-auto pr-1 -mr-1 flex-1 min-h-0">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Chauffeur</label>
                  <p className="mt-1 font-medium">{getDriverName(selectedIncident)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Type</label>
                  <p className="mt-1">{getTypeConfig(selectedIncident.type).label}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Statut</label>
                  <div className="mt-1">
                    <StatusBadge status={selectedIncident.status} />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    Signalé le
                  </label>
                  <p className="mt-1">{formatDate(selectedIncident.created_at)}</p>
                </div>
                {selectedIncident.resolved_at && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 flex items-center">
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Résolu le
                    </label>
                    <p className="mt-1">{formatDate(selectedIncident.resolved_at)}</p>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  Description
                </label>
                <p className="mt-1 p-3 bg-gray-50 rounded-lg whitespace-pre-wrap">{selectedIncident.description}</p>
              </div>

              {loadingDetail ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Chargement de la photo...
                </div>
              ) : (
                selectedIncident.photo_url && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 flex items-center">
                      <ImageIcon className="h-4 w-4 mr-1" />
                      Photo jointe
                    </label>
                    <img
                      src={selectedIncident.photo_url}
                      alt="Photo de l'incident"
                      className="mt-2 w-full max-h-80 object-contain rounded-lg border border-gray-200"
                    />
                  </div>
                )
              )}

              {selectedIncident.resolution_note && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Note de résolution</label>
                  <p className="mt-1 p-3 bg-gray-50 rounded-lg whitespace-pre-wrap">
                    {selectedIncident.resolution_note}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => onDetailDialogOpenChange(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mise à jour du statut */}
      <Dialog open={showActionDialog} onOpenChange={onActionDialogOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Mettre à jour le statut</DialogTitle>
            <DialogDescription>{selectedIncident && getDriverName(selectedIncident)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Nouveau statut</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {[
                  { value: 'acknowledged', label: 'Pris en compte' },
                  { value: 'resolved', label: 'Résolu' },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setActionStatus(value)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      actionStatus === value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Note (optionnel)</label>
              <Textarea
                placeholder="Ajouter une note sur le traitement de cet incident..."
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onActionDialogOpenChange(false)}>
              Annuler
            </Button>
            <Button onClick={confirmUpdateStatus}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncidentsManagementPremium;
